import { Component, Inject, OnInit, Optional, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { RuntimeConfigService } from '../service/config/runtime-config.service';
import { LoggerService } from '../service/utils/logger.service';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './reset-password.component.html',
  styleUrls: ['../login/login.component.css'],
})
export class ResetPasswordComponent implements OnInit {
  /** Parsed recovery JWT from Supabase redirect hash/query */
  private recoveryAccessToken: string | null = null;

  state: 'no_token' | 'ready' = 'no_token';
  error = '';
  loading = false;
  protected readonly brand = inject(BrandService);

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public runtime: RuntimeConfigService,
    @Inject(DOCUMENT) private doc: Document,
    @Optional() @Inject(APP_BASE_HREF) private appBaseHref: string | null,
  ) {
    this.form = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirm: ['', [Validators.required]],
      },
      { validators: ResetPasswordComponent.passwordsMatch },
    );
  }

  ngOnInit(): void {
    const win = this.doc.defaultView;
    if (!win) {
      return;
    }
    const hash = win.location.hash?.replace(/^#/, '') ?? '';
    const search = win.location.search?.replace(/^\?/, '') ?? '';
    const raw = hash.length ? hash : search;
    const params = new URLSearchParams(raw);
    const accessToken = params.get('access_token');
    const type = params.get('type');

    if (type === 'recovery' && accessToken) {
      this.recoveryAccessToken = accessToken;
      this.state = 'ready';
      try {
        win.history.replaceState(null, '', win.location.pathname + win.location.search);
      } catch {
        /* ignore */
      }
      return;
    }

    this.state = 'no_token';
  }

  private static passwordsMatch(control: AbstractControl): ValidationErrors | null {
    const group = control as FormGroup;
    const p = group.get('password')?.value as string | undefined;
    const c = group.get('confirm')?.value as string | undefined;
    if (p == null || c == null || (p === '' && c === '')) {
      return null;
    }
    return p === c ? null : { mismatch: true };
  }

  async onSubmit(): Promise<void> {
    this.error = '';
    if (!this.form.valid || !this.recoveryAccessToken) {
      this.form.markAllAsTouched();
      return;
    }
    const base = this.runtime.supabaseProjectUrl;
    const apiKey = this.runtime.supabaseApiKey;
    if (!base || !apiKey) {
      this.error = 'Reset is not configured (missing Supabase URL or API key in runtime-config).';
      return;
    }
    const password = String(this.form.value.password ?? '');
    this.loading = true;
    try {
      const res = await fetch(`${base}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
          Authorization: `Bearer ${this.recoveryAccessToken}`,
        },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        LoggerService.error('Password update failed', { status: res.status, text });
        this.error = 'Could not set the new password. The link may have expired — request a new one.';
        return;
      }
      this.recoveryAccessToken = null;
      await this.router.navigate(['/login'], { queryParams: { reset: 'ok' } });
    } catch (e) {
      LoggerService.error('Password update error', e);
      this.error = 'Network error. Try again.';
    } finally {
      this.loading = false;
    }
  }
}
